import cn from 'classnames';
import React from 'react';
import { useAppSelector } from '@app/hooks';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@react-md/button';
import { TextField } from '@react-md/form';
import { useToggle } from '@react-md/utils';
import { FeatureContent } from '@components/feature-content';
import { FormField, NonFieldErrors } from '@components/form-field';
import { SubmitButton } from '@components/submit-button';
import { SsoEnabled } from '@services/df/codegen-enums';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { selectAppSettings } from '@modules/app';
import { useUserFormLabels } from '@modules/user';
import { setApiErrors } from '@utils';
import { useValidation } from '@hooks';
import { createLoginValidation, createTwoFactorValidation } from '../form-validation';
import { AuthTitle } from '../components';
import { ResetPasswordButton } from './reset-password-button';
import { SignInWithSso } from './sign-in-with-sso';
import { signInId } from '../helpers';
import { SignInForm, TwoFactorForm } from '../types';
import { SignInFormId, TwoFactorFormId } from '../constants';
import { Typography } from '@react-md/typography';

const useSignInSettings = () => {
    const settings = useAppSelector(selectAppSettings);
    const isSsoEnabled =
        settings.sso_enabled === SsoEnabled.Widget || settings.sso_enabled === SsoEnabled.BackofficeAndWidget;
    const [isBaseForm, showBaseForm, hideBaseForm] = useToggle(!isSsoEnabled);

    return {
        slug: settings.company_title,
        title: settings.company_full_title || settings.company_title,
        isSsoEnabled,
        isBaseForm,
        showBaseForm,
        hideBaseForm,
    };
};

interface UseSignInFormProps {
    request: (data: SignInForm) => Promise<void>;
    onSuccess?: () => void;
}

const useSignInForm = ({ request, onSuccess }: UseSignInFormProps) => {
    const validationSchema = useValidation(createLoginValidation);
    const formApi = useForm<SignInForm>({
        defaultValues: {
            email: '',
            password: '',
        },
        resolver: yupResolver(validationSchema),
    });

    const {
        setError,
        formState: { isSubmitting: isTokenObtaining },
    } = formApi;

    const submit: SubmitHandler<SignInForm> = async form => {
        try {
            const response = await request(form);

            onSuccess?.();
        } catch (error) {
            const errors = (error as FetchBaseQueryError).data;
            setApiErrors(setError, errors);
        }
    };
    return {
        formApi,
        isTokenObtaining,
        submit,
    };
};

interface Props extends UseSignInFormProps {}

export const AuthSignInForm: React.FC<Props> = ({ children, request, onSuccess }) => {
    const { slug, title, isSsoEnabled, isBaseForm, showBaseForm } = useSignInSettings();
    const { formApi, isTokenObtaining, submit } = useSignInForm({ request, onSuccess });

    const {
        control,
        handleSubmit,
        watch,
        trigger,
        formState: { errors },
    } = formApi;

    const { t } = useTranslation();
    const labels = useUserFormLabels(t);

    const email = watch('email');

    return (
        <div className={cn('fieldset-grid', 'base-paddings')}>
            <form
                id={SignInFormId}
                className={cn('fieldset-grid-cell', 'fieldset-grid')}
                onSubmit={handleSubmit(submit)}
            >
                <AuthTitle
                    title={t('titles.signIn', 'Sign in to {{company}}', {
                        company: title,
                    })}
                />

                <FormField
                    prefix={SignInFormId}
                    name="email"
                    control={control}
                    component={TextField}
                    label={labels.email}
                />

                {isBaseForm && (
                    <FormField
                        prefix={SignInFormId}
                        name="password"
                        control={control}
                        component={TextField}
                        label={labels.password}
                        widgetProps={{
                            type: 'password',
                            className: 'rmd-text-field-container--autogrow-addon',
                            isRightAddon: false,
                            rightChildren: <ResetPasswordButton email={email} trigger={trigger} />,
                        }}
                    />
                )}

                <NonFieldErrors errors={errors} />
            </form>

            <div className={cn('emulated-flex-gap')}>
                {isBaseForm ? (
                    <SubmitButton prefix={signInId()} form={SignInFormId} isSending={isTokenObtaining}>
                        <FeatureContent contentKey={signInId('submit')} fallback={t('buttons.signIn', 'Sign in')} />
                    </SubmitButton>
                ) : (
                    <FeatureContent
                        contentKey={signInId('show_base_form')}
                        fallback={t('buttons.enterPassword', 'Enter password')}
                        wrapper={props => (
                            <Button
                                className="rmd-button--text-large"
                                theme="primary"
                                themeType="contained"
                                onClick={showBaseForm}
                                {...props}
                            />
                        )}
                    />
                )}

                {isSsoEnabled && <SignInWithSso slug={slug} email={email} trigger={trigger} />}
            </div>
        </div>
    );
};

interface UseTwoFactorFormProps {
    request: (data: TwoFactorForm) => Promise<void>;
    onSuccess?: () => void;
}

const useTwoFactorForm = ({ request, onSuccess }: UseTwoFactorFormProps) => {
    const validationSchema = useValidation(createTwoFactorValidation);
    const formApi = useForm<TwoFactorForm>({
        defaultValues: {
            code: '',
        },
        resolver: yupResolver(validationSchema),
    });

    const {
        setError,
        formState: { isSubmitting: isTokenObtaining },
    } = formApi;

    const submit: SubmitHandler<TwoFactorForm> = async form => {
        try {
            const response = await request(form);

            onSuccess?.();
        } catch (error) {
            const errors = (error as FetchBaseQueryError).data;
            setApiErrors(setError, errors);
        }
    };
    return {
        formApi,
        isTokenObtaining,
        submit,
    };
};

interface AuthTwoFactorFormProps extends UseTwoFactorFormProps {
    twoFactorType?: string;
}

export const AuthTwoFactorForm: React.FC<AuthTwoFactorFormProps> = ({
    children,
    twoFactorType,
    request,
    onSuccess,
}) => {
    const { title } = useSignInSettings();
    const { formApi, isTokenObtaining, submit } = useTwoFactorForm({ request, onSuccess });

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = formApi;

    const { t } = useTranslation();

    return (
        <div className={cn('fieldset-grid', 'base-paddings')}>
            <form
                id={TwoFactorFormId}
                className={cn('fieldset-grid-cell', 'fieldset-grid')}
                onSubmit={handleSubmit(submit)}
            >
                <AuthTitle
                    title={t('titles.signIn', 'Sign in to {{company}}', {
                        company: title,
                    })}
                />

                {twoFactorType === 'email' ? (
                    <Typography type="body-2">
                        {t(
                            'messages.codeIsSent',
                            'The code is sent. Please check your inbox and enter the code sent to your email.',
                        )}
                    </Typography>
                ) : null}

                <FormField
                    prefix={TwoFactorFormId}
                    name="code"
                    control={control}
                    component={TextField}
                    label={t('labels.code', 'Code')}
                />

                <NonFieldErrors errors={errors} />
            </form>

            <div className={cn('emulated-flex-gap')}>
                <SubmitButton id={signInId('send_code')} form={TwoFactorFormId} isSending={isTokenObtaining}>
                    <FeatureContent contentKey={signInId('send_code')} fallback={t('buttons.sendCode', 'Send code')} />
                </SubmitButton>
            </div>
        </div>
    );
};
